<template>
  <div :style="`position: relative`">
    <div style="position: relative">
      <vue-p5 @setup="setup" @draw="draw" @mouseclicked="mouseclicked"></vue-p5>
      <vue-drag-resize
        style="font-weight: 700; font-size: 1.3rem"
        :parentLimitation="true"
        :isDraggable="is_input_draggable"
        :isResizable="false"
        :x="local_shapes.inputs[input_index].x"
        :y="local_shapes.inputs[input_index].y"
        :minW="40"
        :h="20"
        :parentW="local_shapes.styles.width"
        :parentH="local_shapes.styles.height"
        v-for="(input, input_index) in local_shapes.inputs"
        :key="`$DRAGINPUT${input_index}`"
        @clicked="onInputClicked"
        @dragstop="onDragStop($event, input_index)"
        >{{ input.text }}</vue-drag-resize
      >
    </div>
    <div v-if="local_shapes.is_selectable" class="columns">
      <div class="column is-12">
        <div class="buttons">
          <b-button size="is-small" type="is-dark" @click="resetLines()"
            >Reset</b-button
          >
          <b-button size="is-small" type="is-dark" @click="undoSelection()"
            >Undo</b-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueP5 from "vue-p5";
import { Dot } from "./utils";
export default {
  name: "show-shapes",
  props: {
    shapes: {
      type: Object,
    },
    is_selectable: {
      type: Boolean,
      default: false,
    },
    is_input_draggable: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueP5,
  },
  data() {
    return {
      sketch: null,
      local_shapes: this.shapes,
      dots: [],
      lines: this.shapes && this.shapes.lines ? this.shapes.lines : [],
      is_input_clicked: false,
    };
  },
  methods: {
    onInputClicked() {
      this.is_input_clicked = true;
    },
    onDragStop(e, index) {
      this.local_shapes.inputs[index].x = e.left;
      this.local_shapes.inputs[index].y = e.top;
      this.is_input_clicked = false;
    },

    clearSketch() {
      this.sketch.clear();

      // Removing the previous inputs
      let prev_inps = document.getElementsByClassName("number_line_inp");
      for (let inp of prev_inps) {
        inp.remove();
      }
    },
    resetLines() {
      this.lines = [];
      this.setup(this.sketch);
    },
    undoSelection() {
      this.lines = this.lines.slice(0, this.lines.length - 1);
    },
    mouseclicked(sketch) {
      // restrict event to canvas
      let canvas_height = this.local_shapes.styles.height;
      let canvas_width = this.local_shapes.styles.width;

      let num_cols = this.local_shapes.num_cols;
      let num_rows = this.local_shapes.num_rows;

      if (
        sketch.mouseX > 0 &&
        sketch.mouseX < canvas_width &&
        sketch.mouseY > 0 &&
        sketch.mouseY < canvas_height &&
        !this.is_input_clicked &&
        this.is_selectable
      ) {
        //get nearest point
        for (let i = 0; i < num_rows; i++) {
          for (let j = 0; j < num_cols; j++) {
            let nearest_point = this.dots[i][j].clicked(
              sketch.mouseX,
              sketch.mouseY
            );

            if (nearest_point) {
              this.linesPositions(nearest_point.x, nearest_point.y);
              return;
            }
          }
        }
      }
    },
    linesPositions(x, y) {
      if (!this.lines.length) {
        // Setting the first dot
        this.lines.push({ from: { x, y }, to: null });
      } else if (
        this.lines[this.lines.length - 1].from &&
        !this.lines[this.lines.length - 1].to
      ) {
        // Setting the first line
        this.lines[this.lines.length - 1].to = { x, y };
      } else {
        // Setting next lines
        this.lines.push({
          from: this.lines[this.lines.length - 1].to,
          to: { x, y },
        });
      }
    },
    setup(sketch) {
      if (sketch) {
        // Assigning the sketch
        this.sketch = sketch;
        this.dots = [];

        // Clearing the previous sketch
        this.clearSketch();

        //global variables
        let height = this.local_shapes.styles.height;
        let width = this.local_shapes.styles.width;
        const num_cols = this.local_shapes.num_cols;
        const num_rows = this.local_shapes.num_rows;
        const dot_radius = this.local_shapes.point_styles.width / 2;

        // Creating the canvas
        sketch.createCanvas(width, height);
        sketch.background(
          this.is_selectable ? this.local_shapes.styles.bg_color : 255
        );

        if (num_rows > 1 && num_cols > 1) {
          //calculate gaps
          const margin_x = (this.local_shapes.margin_percent / 100) * width; // margin-x in px
          const margin_y = (this.local_shapes.margin_percent / 100) * height; // margin-y in px

          let gap_x = (width - 2 * margin_x) / (num_cols - 1); // x-gap between points
          let gap_y = (height - 2 * margin_y) / (num_rows - 1); // y-gap between points

          //draw points matrix
          for (let i = 0; i < num_rows; i++) {
            for (let j = 0; j < num_cols; j++) {
              const dot_x = margin_x + gap_x * j;
              const dot_y = margin_y + gap_y * i;
              let dot = new Dot(dot_x, dot_y, dot_radius);
              if (!this.dots[i]) {
                this.dots[i] = [];
              }
              this.dots[i].push(dot);

              // draw point if is_selectable
              if (this.is_selectable) {
                sketch.strokeWeight(dot_radius);
                sketch.stroke(this.local_shapes.point_styles.bg_color);
                sketch.point(this.dots[i][j].x, this.dots[i][j].y);
              }
            }
          }
        }

        // Draw angles
        const angles = this.shapes.angles;
        for (let angle_row of angles) {
          if (
            this.dots[angle_row.dot_row] &&
            this.dots[angle_row.dot_row][angle_row.dot_col]
          ) {
            const dot_details = this.dots[angle_row.dot_row][angle_row.dot_col];
            // Find the line towards point
            const line_towards = this.lines.find(
              (line) =>
                line.to &&
                line.to.x == dot_details.x &&
                line.to.y == dot_details.y
            );
            // Find the line away from point
            const line_away = this.lines.find(
              (line) =>
                line.from &&
                line.from.x == dot_details.x &&
                line.from.y == dot_details.y
            );

            if (line_towards && line_away) {
              this.startDrawingAngleBetweenLines(
                line_towards,
                line_away,
                angle_row
              );
            }
          }
        }
      }
    },
    translateLineNewOrigin(line, origin) {
      line.from.x -= origin.x;
      line.from.y -= origin.y;
      line.to.x -= origin.x;
      line.to.y -= origin.y;

      line.from.y *= -1;
      line.to.y *= -1;

      line.from.x = line.from.x !== 0 ? line.from.x : 0;
      line.from.y = line.from.y !== 0 ? line.from.y : 0;
      line.to.x = line.to.x !== 0 ? line.to.x : 0;
      line.to.y = line.to.y !== 0 ? line.to.y : 0;
      return line;
    },
    startDrawingAngleBetweenLines(line_towards, line_away, angle_row) {
      const origin = { x: line_towards.to.x, y: line_towards.to.y };
      // Translated lines wrt new axis
      const translated_line_towards = this.translateLineNewOrigin(
        JSON.parse(JSON.stringify(line_towards)),
        origin
      );
      const translated_line_away = this.translateLineNewOrigin(
        JSON.parse(JSON.stringify(line_away)),
        origin
      );

      // Translating the axis temporarily
      this.sketch.translate(origin.x, origin.y);
      this.sketch.scale(1, -1);

      this.drawAngleBetweenLines(
        translated_line_towards,
        translated_line_away,
        angle_row
      );

      // Translating back to original
      this.sketch.translate(-1 * origin.x, origin.y);
      this.sketch.scale(1, -1);
    },
    returnArcFromTo(a1, a2) {
      const max_angle = Math.max(a1, a2);
      const min_angle = Math.min(a1, a2);
      const angle_diff = max_angle - min_angle;
      if (angle_diff <= 180) {
        return {
          acute: {
            from: min_angle,
            to: max_angle,
          },
          obtuse: {
            from: max_angle,
            to: 360 + min_angle,
          },
        };
      } else {
        return {
          acute: {
            from: max_angle,
            to: 360 + min_angle,
          },
          obtuse: {
            from: min_angle,
            to: max_angle,
          },
        };
      }
    },
    drawAngleBetweenLines(line_towards, line_away, angle_row) {
      const x_angle_line_towards = this.calcXAngleOfLine(line_towards); // Angle which line_towards subtends on x-axis
      const x_angle_line_away = this.calcXAngleOfLine(line_away); // Angle which line_towards subtends on x-axis
      // Finding from & to angle for drawing arc
      const arc_angle = this.returnArcFromTo(
        x_angle_line_towards,
        x_angle_line_away
      );
      const arc_from_to =
        angle_row.angle_type == "A" ? arc_angle.acute : arc_angle.obtuse;
      // Drawing the arc
      const arc_from_angle_radian = (arc_from_to.from * this.sketch.PI) / 180;
      const arc_to_angle_radian = (arc_from_to.to * this.sketch.PI) / 180;
      this.sketch.stroke(this.local_shapes.line_styles.border_color);
      this.sketch.strokeWeight(this.local_shapes.line_styles.width);
      this.sketch.noFill();
      if (x_angle_line_towards === 90 || x_angle_line_away === -90) {
        this.sketch.square(
          line_towards.to.x,
          line_towards.to.y,
          angle_row.arc_width
        );
      } else {
        this.sketch.arc(
          line_towards.to.x,
          line_towards.to.y,
          c,
          angle_row.arc_width,
          arc_from_angle_radian,
          arc_to_angle_radian
        );
      }
    },
    calcXAngleOfLine(line) {
      // Calculates the angle in degrees
      let angle = this.calcLineAcuteAngle({
        x1: line.from.x,
        y1: line.from.y,
        x2: line.to.x,
        y2: line.to.y,
      });

      if (
        (line.from.y >= 0 || line.to.y >= 0) &&
        (line.from.x < 0 || line.to.x < 0)
      ) {
        // If 2nd quadrant
        angle += 180;
      } else if (
        (line.from.y < 0 || line.to.y < 0) &&
        (line.from.x > 0 || line.to.x > 0)
      ) {
        // If 4th quadrant
        angle += 360;
      } else if (
        (line.from.y < 0 || line.to.y < 0) &&
        (line.from.x < 0 || line.to.x < 0)
      ) {
        // If 3rd quadrant
        angle -= 180;
      }
      return angle;
    },
    calcLineAcuteAngle(points) {
      if (points.x2 == points.x1) {
        // If vertical line
        if (points.y2 > 0 || points.y1 > 0) {
          // If line on +ve y-axis
          return 90;
        } else {
          return -90;
        }
      }
      const slope = (points.y2 - points.y1) / (points.x2 - points.x1);
      return (Math.atan(slope) * 180) / Math.PI;
    },
    draw(sketch) {
      //draw lines
      for (let i = 0; i < this.lines.length; i += 1) {
        if (
          this.lines[i] &&
          this.lines[i].from &&
          this.lines[i].to &&
          this.lines[i].from.x &&
          this.lines[i].to.y &&
          this.lines[i].to.x &&
          this.lines[i].from.y
        ) {
          sketch.stroke(this.local_shapes.line_styles.border_color);
          sketch.strokeWeight(this.local_shapes.line_styles.width);
          sketch.line(
            this.lines[i].from.x,
            this.lines[i].from.y,
            this.lines[i].to.x,
            this.lines[i].to.y
          );
        }
      }
    },
    hexToRgb(hex) {
      var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    },
  },
  computed: {},
  render(h) {
    return h(VueP5, { on: this });
  },
  watch: {
    shapes: {
      handler(new_val) {
        this.local_shapes = new_val;
        if (new_val && new_val.lines) {
          this.lines = new_val.lines;
        }
      },
      deep: true,
    },
    local_shapes: {
      handler() {
        this.setup(this.sketch);
      },
      deep: true,
    },
    lines: {
      handler(new_val) {
        this.$emit("receiveLines", new_val);
      },
      deep: true,
    },
  },
};
</script>

<style scoped></style>
